// MapTiler Geocoding (https://docs.maptiler.com/cloud/api/geocoding/).
// Free tier: 100k requests/month. ToS permits per-user caching for PWAs.
// Returns { lat, lng, label } to match the previous Nominatim shape.
const MAPTILER_KEY = process.env.REACT_APP_MAPTILER_KEY;
const BASE = "https://api.maptiler.com/geocoding";
const LANG = () => (navigator.language || "en").split("-")[0];

// ─── In-session LRU cache ────────────────────────────────────────────────────
const CACHE_MAX = 50;
const cache = new Map();
const cacheGet = (key) => {
  if (!cache.has(key)) return undefined;
  const value = cache.get(key);
  cache.delete(key); cache.set(key, value);
  return value;
};
const cacheSet = (key, value) => {
  if (cache.has(key)) cache.delete(key);
  else if (cache.size >= CACHE_MAX) cache.delete(cache.keys().next().value);
  cache.set(key, value);
};

const requireKey = () => {
  if (!MAPTILER_KEY) throw new Error("REACT_APP_MAPTILER_KEY is not set");
};

// ─── Forward geocoding ───────────────────────────────────────────────────────
export async function geocodeAddress(query) {
  requireKey();
  const trimmed = query.trim();
  const key = `f:${LANG()}:${trimmed.toLowerCase()}`;
  const cached = cacheGet(key);
  if (cached) return cached;

  const url = `${BASE}/${encodeURIComponent(trimmed)}.json?key=${MAPTILER_KEY}&limit=1&language=${LANG()}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`geocode_failed_${res.status}`);
  const data = await res.json();
  const feature = data.features?.[0];
  if (!feature) throw new Error("geocode_no_results");
  const [lng, lat] = feature.center;
  const result = { lat, lng, label: feature.place_name };
  cacheSet(key, result);
  return result;
}

// ─── Reverse geocoding ───────────────────────────────────────────────────────
export async function reverseGeocode(lat, lng) {
  requireKey();
  // Round to ~11m precision so nearby clicks share a cache entry.
  const rLat = lat.toFixed(4);
  const rLng = lng.toFixed(4);
  const key = `r:${LANG()}:${rLat},${rLng}`;
  const cached = cacheGet(key);
  if (cached !== undefined) return cached;

  const url = `${BASE}/${rLng},${rLat}.json?key=${MAPTILER_KEY}&language=${LANG()}`;
  const res = await fetch(url);
  if (!res.ok) {
    cacheSet(key, null);
    return null;
  }
  const data = await res.json();
  const label = data.features?.[0]?.place_name ?? null;
  cacheSet(key, label);
  return label;
}
